/** Tiny query layer on top of {@link api} (T-832 / T-833). */
import type {
  ApplicantTags,
  Application,
  Candidate,
  CandidateNote,
  ColdSearch,
  ColdSearchDetail,
  ColdSearchResult,
  MatchScore,
  PipelineStages,
  Schedule,
  Vacancy,
  VacancyCandidateItem,
  VacancyColdSearchResults,
  VacancyCriteria,
} from "@/types/domain";

import { api } from "@/lib/api";

export interface ShortlistItem {
  match_score_id: string;
  candidate: Candidate;
  vacancy: Vacancy;
  score: number;
  scale_max: number;
  category: string | null;
  // `cold_search` since 2026-08-06: the inbox is no longer fed only by
  // inbound applications. A widened union rather than a `string` so an
  // unhandled tag stays a compile error at the render site.
  tag: "new_application" | "re_suggestion" | "cold_search";
  created_at: string;
}

/**
 * The backend doesn't expose a dedicated "shortlist for recruiter" feed
 * yet (Phase 9 work), so the Mini App reconstructs it client-side from
 * notification_deliveries: every dispatch we made to *me* with status
 * 'sent' is a shortlist card I should still see in the inbox.
 *
 * Once a dedicated endpoint lands, swap this single function over and
 * the UI doesn't move.
 */
export async function fetchShortlist(): Promise<ShortlistItem[]> {
  const items = await api.get<ShortlistItem[]>("/api/v1/me/shortlist");
  return items;
}

export async function fetchCandidate(id: string): Promise<Candidate> {
  return api.get<Candidate>(`/api/v1/candidates/${id}`);
}

export async function fetchCandidateApplications(
  id: string,
): Promise<Application[]> {
  return api.get<Application[]>(`/api/v1/candidates/${id}/applications`);
}

export async function fetchCandidateMatch(
  candidateId: string,
  vacancyId: string,
): Promise<MatchScore | null> {
  return api.get<MatchScore | null>(
    `/api/v1/me/candidates/${candidateId}/match?vacancy_id=${vacancyId}`,
  );
}

export interface CandidateCv {
  cv_document_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  parse_status: string;
  created_at: string;
  download_url: string;
}

export async function fetchCandidateCv(
  candidateId: string,
): Promise<CandidateCv | null> {
  return api.get<CandidateCv | null>(`/api/v1/me/candidates/${candidateId}/cv`);
}

export async function fetchCandidateNotes(
  id: string,
): Promise<CandidateNote[]> {
  return api.get<CandidateNote[]>(`/api/v1/candidates/${id}/notes`);
}

export async function postCandidateNote(
  candidateId: string,
  authorId: string,
  body: string,
): Promise<CandidateNote> {
  return api.post<CandidateNote>(`/api/v1/candidates/${candidateId}/notes`, {
    candidate_id: candidateId,
    author_id: authorId,
    body,
  });
}

export async function deleteCandidateNote(
  candidateId: string,
  noteId: string,
): Promise<void> {
  return api.del<void>(`/api/v1/candidates/${candidateId}/notes/${noteId}`);
}

/** One vacancy — the cold-search screen reads its constraints (work format,
 * gender, age) to prefill the form with the same narrowing the nightly run
 * applies. Requires ``vacancy.read``, which every recruiter role has. */
export async function fetchVacancy(vacancyId: string): Promise<Vacancy> {
  return api.get<Vacancy>(`/api/v1/vacancies/${vacancyId}`);
}

/** «Забрати собі» — take this vacancy on (Roman, 2026-08-14: recruiters pick
 * their own work off the shared list instead of waiting to be assigned).
 *
 * Body-less on purpose: the server claims for the authenticated caller, so
 * there is no field through which one recruiter could name another. A vacancy
 * somebody else already leads answers 409 with a ready Ukrainian message —
 * show it, do not retry. Re-claiming what is already yours is a 200 no-op. */
export async function claimVacancy(vacancyId: string): Promise<Vacancy> {
  return api.post<Vacancy>(
    `/api/v1/vacancies/${encodeURIComponent(vacancyId)}/claim`,
  );
}

/** «Відмовитись» — give the vacancy back to the shared pool. Only its current
 * owner may (an HR Lead reassigns via the admin route), and afterwards this
 * vacancy's Telegram cards go to the manager fallback, not to the person who
 * just let go of it. */
export async function releaseVacancy(vacancyId: string): Promise<Vacancy> {
  return api.del<Vacancy>(
    `/api/v1/vacancies/${encodeURIComponent(vacancyId)}/claim`,
  );
}

export async function fetchVacancyCriteria(
  vacancyId: string,
): Promise<VacancyCriteria> {
  return api.get<VacancyCriteria>(`/api/v1/vacancies/${vacancyId}/criteria`);
}

export async function saveVacancyCriteria(
  vacancyId: string,
  body: { criteria: VacancyCriteria["criteria"]; min_score: number },
): Promise<VacancyCriteria> {
  return api.put<VacancyCriteria>(
    `/api/v1/vacancies/${vacancyId}/criteria`,
    body,
  );
}

/** Filters forwarded to ``POST /cold-searches`` (``ColdSearchFilters``).
 * Keys the backend schema does not declare are silently DROPPED, not
 * forwarded: ``APIModel`` sets no ``extra="allow"``, so pydantic's default
 * ``extra="ignore"`` applies, and the router persists
 * ``body.filters.model_dump(mode="json")`` — declared fields only. Every
 * field below therefore mirrors a real ``ColdSearchFilters`` field, and
 * every new filter needs a backend schema change first (a key added here
 * alone reaches nothing and fails silently). */
export interface ColdSearchFilters {
  /** Free-text search query. The backend schema is a plain string
   * (max 255) — NOT an array; sending a list 422s. */
  keywords?: string;
  city?: string;
  /** Only resumes published within the last N days (Part-3 recency). */
  posted_within_days?: number;
  /** Source-side rubric id (Robota.ua ``dictionary/rubric``, e.g. "33"
   * = Автобізнес). Sources without such a taxonomy ignore it. */
  specialization?: string;
  /** Age window, 14..99, ``age_max >= age_min`` (422 otherwise). Applies
   * to Work.ua / Robota.ua; candidates of unknown age are kept.
   *
   * ``null`` and ``undefined`` are NOT the same on the wire: an omitted key
   * means «нічого не сказано» and the vacancy's own constraint applies,
   * while an explicit ``null`` is «зняти обмеження на цей пошук». This
   * screen prefills from the vacancy, so it always sends explicitly. */
  age_min?: number | null;
  age_max?: number | null;
  /** ``null`` = «Не важливо» for THIS run (see the age note above). */
  gender?: "male" | "female" | null;
  /** Only résumés open to remote work, country-wide — ``city`` is ignored.
   * Robota.ua ``scheduleIds:["3"]``; on Work.ua remote is a pseudo-town. */
  remote_only?: boolean;
  /** «Місто та околиці» → Robota.ua ``inside``. Work.ua has no such flag
   * and needs the oblast enumerated, which is off by default server-side. */
  include_region?: boolean;
  /** Where Robota.ua matches ``keywords``: with synonyms (default), in the
   * résumé title only, or anywhere in the text. Work.ua ignores it. */
  search_scope?: "default" | "speciality" | "everywhere";
  /** Years-of-experience floor, 0..60. */
  experience_min?: number;
  /** «Графік роботи» the résumé must declare (see the age note above for
   * why this screen always sends it explicitly): an empty ARRAY is «зняти
   * обмеження на цей пошук», an omitted key defers to the vacancy.
   * Robota.ua filters all six server-side, Work.ua only `full`/`part`, and
   * the internal corpus matches what CV extraction read off the résumé. */
  schedule?: Schedule[];
}

export async function startColdSearch(
  vacancyId: string,
  filters: ColdSearchFilters,
): Promise<ColdSearch> {
  return api.post<ColdSearch>(`/api/v1/cold-searches`, {
    vacancy_id: vacancyId,
    filters,
  });
}

/** Most-recent-first cold searches of the current recruiter for one
 * vacancy — lets the screen resume an in-flight/finished search after
 * a navigation away (component state does not survive unmount). */
export async function listColdSearches(
  vacancyId: string,
  limit = 1,
): Promise<ColdSearch[]> {
  return api.get<ColdSearch[]>(
    `/api/v1/cold-searches?vacancy_id=${encodeURIComponent(vacancyId)}&limit=${limit}`,
  );
}

/** Paging + score-gate knobs shared by the per-run detail read and the
 * accumulated per-vacancy read. Both endpoints default ``limit`` to 50
 * server-side, so a caller that sends nothing silently loses row 51+. */
export interface ColdSearchResultsParams {
  limit?: number;
  offset?: number;
  /** Overrides the vacancy's stored passing threshold; ``0`` disables the
   * gate. Omitted = the server-side per-vacancy threshold stays in charge. */
  minScore?: number;
  /** «Показати відхилених» — merge the «Не цікавий» trash back INTO the live
   * list, which both endpoints hide by default. Not the same as
   * ``?status=dismissed``: that one is the rejected-ONLY view, and nobody
   * comes back to a separate trash screen. Every header counter is computed
   * from the same gate server-side, so they all widen with this flag. */
  includeDismissed?: boolean;
  /** «Показати нижче порога» — drop the score gate on the accumulated read.
   * These résumés were already scored and paid for, and the novelty gate
   * never offers them again, so the threshold was hiding work that exists.
   * Accumulated endpoint only; the per-run detail read ignores it. */
  includeBelowThreshold?: boolean;
  /** «Показати відсіяних» — lifts the two rules the CARD applies on its own:
   * the vacancy's town, and the off-field cosine on a row nothing ever scored.
   * Deliberately does NOT lift age or gender — those a human typed on the
   * vacancy, and a checkbox that un-typed them would be a different feature.
   * Understood by BOTH reads, so it keeps working in the degraded per-run
   * mode where «нижче порога» does not. */
  includeFilteredOut?: boolean;
}

function resultsQuery(params: ColdSearchResultsParams): string {
  const query = new URLSearchParams();
  if (params.limit !== undefined) query.set("limit", String(params.limit));
  if (params.offset !== undefined) query.set("offset", String(params.offset));
  if (params.minScore !== undefined)
    query.set("min_score", String(params.minScore));
  // Sent only when on: ``false`` is already the server default, so an untouched
  // toggle leaves the URL identical to every call made before it existed.
  if (params.includeDismissed) query.set("include_dismissed", "true");
  if (params.includeBelowThreshold)
    query.set("include_below_threshold", "true");
  if (params.includeFilteredOut) query.set("include_filtered_out", "true");
  const qs = query.toString();
  return qs ? `?${qs}` : "";
}

/** Full status + embedded ranked results of ONE run. There is no
 * ``/results`` endpoint — the rows are nested under
 * ``ColdSearchDetailOut.results``.
 *
 * ``params`` is not optional in practice: the backend caps and defaults
 * ``limit`` to 50, so calling this bare truncates a broad run's list without
 * any signal to the user. */
export async function getColdSearch(
  id: string,
  params: ColdSearchResultsParams = {},
): Promise<ColdSearchDetail> {
  return api.get<ColdSearchDetail>(
    `/api/v1/cold-searches/${id}${resultsQuery(params)}`,
  );
}

/** The vacancy's whole cold-search history merged into one candidate list
 * (``GET /api/v1/vacancies/{id}/cold-search-results``).
 *
 * Nightly auto runs return 2-3 résumés each; reading only the last run wiped
 * yesterday's list off the screen. Rows here are collapsed per candidate and
 * every row carries its own ``cold_search_id`` — build action URLs from that,
 * never from the screen's pinned run id.
 *
 * The endpoint runs several counts over the vacancy's whole result history:
 * poll it at 15s, never at the detail endpoint's 2s. */
export async function fetchVacancyColdSearchResults(
  vacancyId: string,
  params: ColdSearchResultsParams = {},
): Promise<VacancyColdSearchResults> {
  return api.get<VacancyColdSearchResults>(
    `/api/v1/vacancies/${encodeURIComponent(vacancyId)}/cold-search-results${resultsQuery(params)}`,
  );
}

/** This candidate's cold-search row on this vacancy, so the candidate card can
 * offer the same actions as the cold-search screen. ``null`` when cold search
 * never surfaced them here (an inbound applicant).
 *
 * The returned ``id``/``cold_search_id`` address a row every action endpoint
 * accepts, while the state flags (dismissed, pushed, talent pool) are
 * aggregated over ALL of the candidate's rows on the vacancy — the same
 * sibling rule the write gates apply, so the card never offers a «Цікавий»
 * the server would refuse with 409. */
export async function fetchCandidateColdResult(
  vacancyId: string,
  candidateId: string,
): Promise<ColdSearchResult | null> {
  // An empty body parses to ``undefined``, which TanStack Query rejects.
  const row = await api.get<ColdSearchResult | null>(
    `/api/v1/vacancies/${encodeURIComponent(vacancyId)}/cold-search-results/by-candidate/${encodeURIComponent(candidateId)}`,
  );
  return row ?? null;
}

/** PeopleForce pipeline stages for the cold search's vacancy — populates
 * the «Цікавий» stage picker. Returns ``default_stage_id`` (position-1
 * «Новий кандидат») for the default selection. Fetched on demand, not on
 * the polled detail payload. */
export async function fetchPipelineStages(
  coldSearchId: string,
): Promise<PipelineStages> {
  return api.get<PipelineStages>(
    `/api/v1/cold-searches/${coldSearchId}/pipeline-stages`,
  );
}

/** «Цікавий» — create the candidate in PeopleForce. Returns the updated
 * result row with ``pushed_to_pf_at`` stamped.
 *
 * When ``applicantStateId`` is supplied, the candidate is also placed on
 * that PeopleForce pipeline stage (an application is created). The body
 * field name matches the backend ``SendToPeopleForceRequest`` exactly.
 * Omitting it preserves the Phase-1 candidate-only push. */
export async function sendColdSearchResultToPeopleForce(
  coldSearchId: string,
  resultId: string,
  applicantStateId?: number,
): Promise<ColdSearchResult> {
  return api.post<ColdSearchResult>(
    `/api/v1/cold-searches/${coldSearchId}/results/${resultId}/send-to-peopleforce`,
    applicantStateId === undefined
      ? undefined
      : { applicant_state_id: applicantStateId },
  );
}

/** «Не цікавий» — soft-hide the result (moves it to trash). */
export async function dismissColdSearchResult(
  coldSearchId: string,
  resultId: string,
): Promise<ColdSearchResult> {
  return api.post<ColdSearchResult>(
    `/api/v1/cold-searches/${coldSearchId}/results/${resultId}/dismiss`,
  );
}

/** The tenant's PeopleForce «Теги» — the hashtags «Кадровий резерв» can file
 * a candidate under. Tenant-wide and effectively static. */
export async function fetchApplicantTags(): Promise<ApplicantTags> {
  return api.get<ApplicantTags>("/api/v1/cold-searches/peopleforce/tags");
}

/** «Кадровий резерв» — file the candidate in PeopleForce under a hashtag,
 * with no vacancy attached. */
export async function addColdSearchResultToTalentPool(
  coldSearchId: string,
  resultId: string,
  tagId: number,
): Promise<ColdSearchResult> {
  return api.post<ColdSearchResult>(
    `/api/v1/cold-searches/${coldSearchId}/results/${resultId}/talent-pool`,
    { tag_id: tagId },
  );
}

/** «Відкрити контакт (~25 грн)» — one paid Work.ua ``GET /resume`` per
 * résumé, ever. The click only queues it and answers with the updated row;
 * the paid outcome lands asynchronously (``workua_open_status`` goes
 * queued → calling → opened | already_known | ambiguous | failed). On a row
 * already bought or known, the same call re-scores it for free.
 *
 * Sent without ``retry_ambiguous``: only an admin may resend a maybe-billed
 * open, and the Mini App does not know the viewer's role — that resend lives
 * in the web admin. */
export async function openWorkUaResult(
  coldSearchId: string,
  resultId: string,
): Promise<ColdSearchResult> {
  return api.post<ColdSearchResult>(
    `/api/v1/cold-searches/${coldSearchId}/results/${resultId}/open-workua`,
  );
}

/** Record that the recruiter opened this résumé on the job site from the
 * app. A person then imported into PeopleForce from that page is AI-found
 * (Роман, 23.09); the vendor link itself never reaches our backend. */
export async function reportColdSearchSourceOpened(
  coldSearchId: string,
  resultId: string,
): Promise<void> {
  await api.post<void>(
    `/api/v1/cold-searches/${coldSearchId}/results/${resultId}/source-opened`,
  );
}

export type VacancyCandidateSort = "score" | "applied_at" | "status";

export interface VacancyCandidateParams {
  status?: string;
  min_score?: number;
  category?: string;
  /** "за 2 тижні" toggle. When false, show all applicants regardless of age. */
  recent_only?: boolean;
  sort?: VacancyCandidateSort;
  limit?: number;
  offset?: number;
}

/** Ranked applicants for one of *my* vacancies, latest score per
 * candidate. Unscored applicants come back with null score/category. */
export async function fetchVacancyCandidates(
  vacancyId: string,
  params: VacancyCandidateParams = {},
): Promise<VacancyCandidateItem[]> {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.min_score !== undefined)
    query.set("min_score", String(params.min_score));
  if (params.category) query.set("category", params.category);
  if (params.recent_only !== undefined)
    query.set("recent_only", String(params.recent_only));
  if (params.sort) query.set("sort", params.sort);
  if (params.limit !== undefined) query.set("limit", String(params.limit));
  if (params.offset !== undefined) query.set("offset", String(params.offset));
  const qs = query.toString();
  return api.get<VacancyCandidateItem[]>(
    `/api/v1/me/vacancies/${vacancyId}/candidates${qs ? `?${qs}` : ""}`,
  );
}

/** How many applicants the "за 2 тижні" toggle is hiding right now.
 *
 * Same filters as {@link fetchVacancyCandidates} minus `recent_only`
 * (the question only means something while the toggle is on) and minus
 * the paging window (the answer spans the whole result, so it must not be
 * derived by diffing two truncated pages). Called only while the toggle is
 * on, so the "show everything" view costs no extra request. */
export async function fetchVacancyCandidatesHiddenCount(
  vacancyId: string,
  params: Pick<
    VacancyCandidateParams,
    "status" | "min_score" | "category"
  > = {},
): Promise<number> {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.min_score !== undefined)
    query.set("min_score", String(params.min_score));
  if (params.category) query.set("category", params.category);
  const qs = query.toString();
  const res = await api.get<{ hidden: number }>(
    `/api/v1/me/vacancies/${vacancyId}/candidates/hidden-count${qs ? `?${qs}` : ""}`,
  );
  return res.hidden;
}
